import { UserCard } from "@features/discovery/components";
import { toDiscoveryPreviewProfile } from "@features/profile/utils/toDiscoveryPreviewProfile";
import { useLayoutContext } from "@router/layouts/MainLayout";
import { PageLoading } from "@shared/components/PageLoading";
import useCurrentUser from "@shared/queries/useCurrentUser";
import clsx from "clsx";
import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";

/**
 * Profile preview: same UserCard as Discovery swipe.
 */
export default function ProfilePreviewPage() {
  const { user, isLoading, error } = useCurrentUser();
  const { setHeader } = useLayoutContext();
  const navigate = useNavigate();

  useEffect(() => {
    setHeader({
      variant: "back",
      title: "How others see you",
      onBack: () => navigate(-1),
    });
    return () => setHeader(null);
  }, [setHeader, navigate]);

  if (isLoading) {
    return <PageLoading message="Loading profile..." />;
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center bg-gray-50 p-8">
        <div className="text-center">
          <h2 className="mb-2 text-xl font-semibold text-gray-900">Failed to load profile</h2>
          <p className="text-gray-600">{error.message}</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex h-full items-center justify-center bg-gray-50 p-8">
        <div className="text-center">
          <h2 className="mb-2 text-xl font-semibold text-gray-900">No profile found</h2>
          <p className="mb-6 text-gray-600">Complete your onboarding to view your profile preview.</p>
          <Link
            to="/onboarding"
            className={clsx(
              "inline-block rounded-xl bg-pink-600 px-6 py-3 text-sm font-semibold text-white",
              "hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2"
            )}
            data-testid="complete-onboarding-link"
          >
            Complete onboarding
          </Link>
        </div>
      </div>
    );
  }

  const previewProfile = toDiscoveryPreviewProfile(user);

  if (!previewProfile) {
    return (
      <div className="flex h-full items-center justify-center bg-gray-50 p-8" data-testid="profile-preview-incomplete">
        <div className="text-center">
          <h2 className="mb-2 text-xl font-semibold text-gray-900">Profile not shown in discovery yet</h2>
          <p className="text-gray-600">Finish name, photo, birthday, and gender to see your swipe card.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-full min-h-0 w-full flex-1 overflow-hidden" data-testid="profile-preview-page">
      <UserCard user={previewProfile} isActive onSwipe={() => undefined} onMatch={() => undefined} />
    </div>
  );
}
